import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import DonationSidebar from '../components/DonationSidebar';

// Define interfaces for our data
interface EnglishVerse {
  chapter: number;
  verse: number;
  text: string;
}

interface EnglishQuranData {
  [key: string]: EnglishVerse[];
}

const EnglishQuranReader: React.FC = () => {
  const [quranData, setQuranData] = useState<EnglishQuranData | null>(null);
  const [currentChapter, setCurrentChapter] = useState<number>(1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const navigate = useNavigate();

  // Load English Quran data
  useEffect(() => {
    const loadEnglishQuran = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetch('/data/quran/en.json');
        
        if (!response.ok) {
          throw new Error(`Failed to load Quran data: ${response.status}`);
        }
        
        const data = await response.json();
        setQuranData(data);
      } catch (err) {
        console.error('Error loading English Quran:', err);
        setError('Failed to load English Quran data. Please check if the file exists at /public/data/quran/en.json');
      } finally {
        setLoading(false);
      }
    };

    loadEnglishQuran();
  }, []);

  // Chapter names in English
  const chapterNames: { [key: number]: string } = {
    1: "Al-Fatihah (The Opening)",
    2: "Al-Baqarah (The Cow)",
    3: "Aal-e-Imran (The Family of Imran)",
    4: "An-Nisa (The Women)",
    5: "Al-Ma'idah (The Table Spread)",
    6: "Al-An'am (The Cattle)",
    7: "Al-A'raf (The Heights)",
    8: "Al-Anfal (The Spoils of War)",
    9: "At-Tawbah (The Repentance)",
    10: "Yunus (Jonah)",
    11: "Hud (Hud)",
    12: "Yusuf (Joseph)",
    13: "Ar-Ra'd (The Thunder)",
    14: "Ibrahim (Abraham)",
    15: "Al-Hijr (The Rocky Tract)",
    16: "An-Nahl (The Bee)",
    17: "Al-Isra (The Night Journey)",
    18: "Al-Kahf (The Cave)",
    19: "Maryam (Mary)",
    20: "Taha (Taha)",
    21: "Al-Anbiya (The Prophets)",
    22: "Al-Hajj (The Pilgrimage)",
    23: "Al-Mu'minun (The Believers)",
    24: "An-Nur (The Light)",
    25: "Al-Furqan (The Criterion)",
    26: "Ash-Shu'ara (The Poets)",
    27: "An-Naml (The Ant)",
    28: "Al-Qasas (The Stories)",
    29: "Al-Ankabut (The Spider)",
    30: "Ar-Rum (The Romans)",
    31: "Luqman (Luqman)",
    32: "As-Sajdah (The Prostration)",
    33: "Al-Ahzab (The Combined Forces)",
    34: "Saba (Sheba)",
    35: "Fatir (The Originator)",
    36: "Ya-Sin (Ya Sin)",
    37: "As-Saffat (Those who set the Ranks)",
    38: "Sad (The Letter Sad)",
    39: "Az-Zumar (The Troops)",
    40: "Ghafir (The Forgiver)",
    41: "Fussilat (Explained in Detail)",
    42: "Ash-Shura (The Consultation)",
    43: "Az-Zukhruf (The Ornaments of Gold)",
    44: "Ad-Dukhan (The Smoke)",
    45: "Al-Jathiyah (The Crouching)",
    46: "Al-Ahqaf (The Wind-Curved Sandhills)",
    47: "Muhammad (Muhammad)",
    48: "Al-Fath (The Victory)",
    49: "Al-Hujurat (The Rooms)",
    50: "Qaf (Qaf)",
    51: "Adh-Dhariyat (The Winnowing Winds)",
    52: "At-Tur (The Mount)",
    53: "An-Najm (The Star)",
    54: "Al-Qamar (The Moon)",
    55: "Ar-Rahman (The Beneficent)",
    56: "Al-Waqi'ah (The Inevitable)",
    57: "Al-Hadid (The Iron)",
    58: "Al-Mujadila (The Pleading Woman)",
    59: "Al-Hashr (The Exile)",
    60: "Al-Mumtahanah (The Woman to be examined)",
    61: "As-Saf (The Ranks)",
    62: "Al-Jumu'ah (The Congregation, Friday)",
    63: "Al-Munafiqun (The Hypocrites)",
    64: "At-Taghabun (The Mutual Disillusion)",
    65: "At-Talaq (The Divorce)",
    66: "At-Tahrim (The Prohibition)",
    67: "Al-Mulk (The Sovereignty)",
    68: "Al-Qalam (The Pen)",
    69: "Al-Haqqah (The Reality)",
    70: "Al-Ma'arij (The Ascending Stairways)",
    71: "Nuh (Noah)",
    72: "Al-Jinn (The Jinn)",
    73: "Al-Muzzammil (The Enshrouded One)",
    74: "Al-Muddathir (The Cloaked One)",
    75: "Al-Qiyamah (The Resurrection)",
    76: "Al-Insan (The Man)",
    77: "Al-Mursalat (The Emissaries)",
    78: "An-Naba (The Tidings)",
    79: "An-Nazi'at (Those who drag forth)",
    80: "Abasa (He frowned)",
    81: "At-Takwir (The Overthrowing)",
    82: "Al-Infitar (The Cleaving)",
    83: "Al-Mutaffifin (The Defrauding)",
    84: "Al-Inshiqaq (The Sundering)",
    85: "Al-Buruj (The Mansions of the Stars)",
    86: "At-Tariq (The Nightcommer)",
    87: "Al-A'la (The Most High)",
    88: "Al-Ghashiyah (The Overwhelming)",
    89: "Al-Fajr (The Dawn)",
    90: "Al-Balad (The City)",
    91: "Ash-Shams (The Sun)",
    92: "Al-Layl (The Night)",
    93: "Ad-Duha (The Morning Hours)",
    94: "Ash-Sharh (The Relief)",
    95: "At-Tin (The Fig)",
    96: "Al-Alaq (The Clot)",
    97: "Al-Qadr (The Power)",
    98: "Al-Bayyinah (The Clear Proof)",
    99: "Az-Zalzalah (The Earthquake)",
    100: "Al-Adiyat (The Courser)",
    101: "Al-Qari'ah (The Calamity)",
    102: "At-Takathur (The Rivalry in world increase)",
    103: "Al-Asr (The Declining Day)",
    104: "Al-Humazah (The Traducer)",
    105: "Al-Fil (The Elephant)",
    106: "Quraysh (Quraysh)",
    107: "Al-Ma'un (The Small kindnesses)",
    108: "Al-Kawthar (The Abundance)",
    109: "Al-Kafirun (The Disbelievers)",
    110: "An-Nasr (The Divine Support)",
    111: "Al-Masad (The Palm Fiber)",
    112: "Al-Ikhlas (The Sincerity)",
    113: "Al-Falaq (The Daybreak)",
    114: "An-Nas (The Mankind)"
  };

  const getAudioUrl = (chapter: number) => {
    const chapterNum = chapter.toString().padStart(3, '0');
    return `/data/quran/Quranmp3/quran-chapter-${chapterNum}.mp3`;
  };

  const handlePlayPause = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play().catch(error => {
        console.error('Error playing audio:', error);
        setIsPlaying(false);
      });
      setIsPlaying(true);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const time = parseFloat(e.target.value);
    if (audioRef.current) {
      audioRef.current.currentTime = time;
      setCurrentTime(time);
    }
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const goToChapter = (chapter: number) => {
    if (chapter >= 1 && chapter <= 114) {
      setCurrentChapter(chapter);
      setIsPlaying(false);
      if (audioRef.current) {
        audioRef.current.currentTime = 0;
        setCurrentTime(0);
      }
    }
  };

  const nextChapter = () => {
    if (currentChapter < 114) {
      goToChapter(currentChapter + 1);
    }
  };

  const prevChapter = () => {
    if (currentChapter > 1) {
      goToChapter(currentChapter - 1);
    }
  };

  const currentVerses = quranData ? quranData[currentChapter.toString()] || [] : [];

  if (loading) {
    return (
      <div className="section-padding">
        <div className="container">
          <div style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📖</div>
            <p>Loading English Quran...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="section-padding">
        <div className="container">
          <div style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>❌</div>
            <p>{error}</p>
            <button 
              onClick={() => navigate('/')}
              style={{
                marginTop: '16px',
                padding: '12px 24px',
                backgroundColor: 'var(--accent-primary)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer'
              }}
            >
              Go Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="section-padding">
      <div className="container">
        <div className="section-title">
          <h1>Quran in English</h1>
          <p>Read and listen to the Holy Quran with English translation</p>
        </div>

        {/* Debug info */}
        <div style={{
          backgroundColor: 'var(--bg-secondary)',
          padding: '16px',
          borderRadius: '8px',
          marginBottom: '20px',
          border: '1px solid var(--border)'
        }}>
          <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '14px' }}>
            <strong>Status:</strong> Loaded {Object.keys(quranData || {}).length} chapters | Current: Chapter {currentChapter} | Verses: {currentVerses.length}
          </p>
        </div>

        {/* Chapter Navigation */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '32px',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <button
            onClick={prevChapter}
            disabled={currentChapter === 1}
            style={{
              padding: '12px 24px',
              border: '1px solid var(--border)',
              backgroundColor: 'var(--bg-primary)',
              color: currentChapter === 1 ? 'var(--text-secondary)' : 'var(--text-primary)',
              borderRadius: '8px',
              cursor: currentChapter === 1 ? 'not-allowed' : 'pointer',
              opacity: currentChapter === 1 ? 0.5 : 1
            }}
          >
            ← Previous
          </button>

          <div style={{ textAlign: 'center' }}>
            <h2 style={{ color: 'var(--text-primary)', margin: 0 }}>
              Chapter {currentChapter}
            </h2>
            <p style={{ color: 'var(--text-secondary)', margin: '4px 0 0 0' }}>
              {chapterNames[currentChapter] || `Surah ${currentChapter}`}
            </p>
          </div>

          <button
            onClick={nextChapter}
            disabled={currentChapter === 114}
            style={{
              padding: '12px 24px',
              border: '1px solid var(--border)',
              backgroundColor: 'var(--bg-primary)',
              color: currentChapter === 114 ? 'var(--text-secondary)' : 'var(--text-primary)',
              borderRadius: '8px',
              cursor: currentChapter === 114 ? 'not-allowed' : 'pointer',
              opacity: currentChapter === 114 ? 0.5 : 1
            }}
          >
            Next →
          </button>
        </div>

        {/* Audio Player */}
        <div className="card" style={{ marginBottom: '32px' }}>
          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <h3 style={{ color: 'var(--text-primary)', marginBottom: '8px' }}>
              Listen to Chapter {currentChapter}
            </h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
              {chapterNames[currentChapter] || `Surah ${currentChapter}`}
            </p>
          </div>

          <audio
            ref={audioRef}
            src={getAudioUrl(currentChapter)}
            onTimeUpdate={handleTimeUpdate}
            onLoadedMetadata={handleLoadedMetadata}
            onEnded={() => setIsPlaying(false)}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onError={(e) => console.error('Audio error:', e)}
          />

          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '12px' }}>
            <button
              onClick={handlePlayPause}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: 'var(--accent-primary)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {isPlaying ? '⏸️' : '▶️'}
            </button>

            <div style={{ flex: 1 }}>
              <input
                type="range"
                min="0"
                max={duration || 0}
                value={currentTime}
                onChange={handleSeek}
                style={{
                  width: '100%',
                  height: '4px',
                  borderRadius: '2px',
                  background: 'var(--border)',
                  outline: 'none'
                }}
              />
            </div>

            <div style={{ 
              fontSize: '14px', 
              color: 'var(--text-secondary)',
              minWidth: '80px',
              textAlign: 'center'
            }}>
              {formatTime(currentTime)} / {formatTime(duration)}
            </div>
          </div>

          {/* Audio file info */}
          <div style={{ 
            fontSize: '12px', 
            color: 'var(--text-secondary)',
            textAlign: 'center',
            marginTop: '8px'
          }}>
            Audio file: {getAudioUrl(currentChapter)}
          </div>
        </div>

        {/* Quran Text */}
        <div className="card">
          <div style={{ 
            padding: '24px',
            lineHeight: '1.8',
            fontSize: '18px',
            color: 'var(--text-primary)'
          }}>
            {currentVerses.length > 0 ? (
              currentVerses.map((verse: EnglishVerse, index: number) => (
                <div key={index} style={{ marginBottom: '24px' }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'flex-start',
                    gap: '12px'
                  }}>
                    <span style={{
                      backgroundColor: 'var(--accent-primary)',
                      color: 'white',
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      flexShrink: 0,
                      marginTop: '2px'
                    }}>
                      {verse.verse}
                    </span>
                    <p style={{ margin: 0, flex: 1 }}>
                      {verse.text}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <div style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>
                <p>No verses found for Chapter {currentChapter}.</p>
                <p>The JSON file might not contain data for this chapter.</p>
              </div>
            )}
          </div>
        </div>

        {/* Navigation at Bottom */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: '32px',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <button
            onClick={prevChapter}
            disabled={currentChapter === 1}
            style={{
              padding: '12px 24px',
              border: '1px solid var(--border)',
              backgroundColor: 'var(--bg-primary)',
              color: currentChapter === 1 ? 'var(--text-secondary)' : 'var(--text-primary)',
              borderRadius: '8px',
              cursor: currentChapter === 1 ? 'not-allowed' : 'pointer',
              opacity: currentChapter === 1 ? 0.5 : 1
            }}
          >
            ← Previous Chapter
          </button>

          <button
            onClick={nextChapter}
            disabled={currentChapter === 114}
            style={{
              padding: '12px 24px',
              border: '1px solid var(--border)',
              backgroundColor: 'var(--bg-primary)',
              color: currentChapter === 114 ? 'var(--text-secondary)' : 'var(--text-primary)',
              borderRadius: '8px',
              cursor: currentChapter === 114 ? 'not-allowed' : 'pointer',
              opacity: currentChapter === 114 ? 0.5 : 1
            }}
          >
            Next Chapter →
          </button>
        </div>

        <DonationSidebar />
      </div>
    </div>
  );
};

export default EnglishQuranReader;